import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import toast from "components/partials/toast/toast";
import { TouchpointItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { SubmitFormProps } from "components/modals/version-edit-modal/version-edit-modal";

import { useRemoveTouchpointFromBatch } from "hooks/api/use-batches";
import {
  setTouchpointVersionsCache,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import {
  mapTouchpointVersionDTO,
  normalizeTouchpointVersionListResponse,
} from "utilities/api-mappers";

import { getErrorMessage } from "utilities";

const touchpointVersionApi = getTouchpointVersion();

interface UseTouchpointActionMenuModalsProps {
  selectedVersion: TouchpointItem | undefined;
  setSelectedVersion: (version: TouchpointItem | undefined) => void;
  editVersionModal: { onClose: () => void };
  newDraftModal: { onClose: () => void; onOpen: () => void };
  currentBatch: { id: string } | null;
  clientId: string;
  fetchBatch: () => Promise<void>;
}

const useTouchpointActionMenuItems = ({
  selectedVersion,
  setSelectedVersion,
  editVersionModal: _editVersionModal,
  newDraftModal,
  currentBatch,
  clientId,
  fetchBatch,
}: UseTouchpointActionMenuModalsProps) => {
  const queryClient = useQueryClient();
  const removeTouchpointFromBatchMutation = useRemoveTouchpointFromBatch({ clientId });

  const refreshTouchpointVersions = useCallback(async () => {
    if (selectedVersion?.campaignId && selectedVersion?.touchpointId) {
      const response = await touchpointVersionApi.getAllVersions(
        {
          clientId,
          campaignId: selectedVersion.campaignId,
          touchpointId: selectedVersion.touchpointId,
        },
        {},
      );
      const normalized = normalizeTouchpointVersionListResponse(response);
      setTouchpointVersionsCache(
        queryClient,
        {
          clientId,
          campaignId: selectedVersion.campaignId,
          touchpointId: selectedVersion.touchpointId,
        },
        normalized,
      );

      return normalized.items;
    }
  }, [clientId, selectedVersion, queryClient]);

  const handleSubmitEditVersion = ({ id, description, fiscalYear }: SubmitFormProps) => {
    if (id && selectedVersion?.campaignId && selectedVersion?.touchpointId) {
      return touchpointVersionApi
        .patchTouchpointVersion(
          {
            clientId,
            campaignId: selectedVersion.campaignId,
            touchpointId: selectedVersion.touchpointId,
            id,
          },
          // Orval models nullable fields as wrapper objects, but the backend
          // accepts raw primitives — cast through unknown to keep TS quiet.
          { description, fiscalYear } as unknown as Parameters<
            typeof touchpointVersionApi.patchTouchpointVersion
          >[1],
        )
        .then(() => {
          toast.success({
            data: {
              title: "Updated version details",
            },
          });
          refreshTouchpointVersions();
        })
        .catch((err: AxiosError) => {
          toast.error({
            data: {
              title: "Failed to edit version",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handleCreateDraftFromVersion = () => {
    newDraftModal.onOpen();
  };

  const handleSubmitNewDraft = ({
    id,
    description,
    applyClientTheme,
    fiscalYear,
  }: SubmitFormProps) => {
    if (id && selectedVersion?.campaignId && selectedVersion?.touchpointId) {
      return touchpointVersionApi
        .createNewDraftVersion(
          {
            clientId,
            campaignId: selectedVersion.campaignId,
            touchpointId: selectedVersion.touchpointId,
            id,
          },
          {
            description,
            applyClientTheme: applyClientTheme!,
            fiscalYear,
          },
        )
        .then(mapTouchpointVersionDTO)
        .then(() => {
          toast.success({
            data: {
              title: "Created draft",
            },
          });
          fetchBatch();
          return refreshTouchpointVersions();
        })
        .then((refreshedVersions: TouchpointVersionAttributes[] | undefined) => {
          if (refreshedVersions && refreshedVersions.length > 0) {
            setSelectedVersion(refreshedVersions[0] as unknown as TouchpointItem);
          }
        })
        .catch((err: AxiosError) => {
          toast.error({
            data: {
              title: "Failed to create draft",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handleDeleteTouchpointFromBatch = async (id: string) => {
    if (!currentBatch?.id) return;

    await removeTouchpointFromBatchMutation.mutateAsync({
      batchId: currentBatch.id,
      versionId: id,
    });
    fetchBatch();
  };

  return {
    handleCreateDraftFromVersion,
    handleSubmitNewDraft,
    handleSubmitEditVersion,
    handleDeleteTouchpointFromBatch,
    refreshTouchpointVersions,
  };
};

export default useTouchpointActionMenuItems;
